import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import Truncate from 'react-truncate';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';

// CONSTANTS
import { contentStatuses, DisplayType } from '@/constants/watchList';
import { LOCATIONS_EPISODE_HOVER } from '@/constants/locations';

// UTILITY
import { getContentYear, getDuration } from '@/utility/content';
import { externalLink, getExternalUrlParams } from '@/utility/analytics';

// STORE
import { AuthSelectors } from '@/store/selectors';

// STYLES
import { EpisodeCardContentStyled } from '../styles';

// HELP
import AppsList from './AppsList';

function getStringWithoutSpaces(subtitles) {
  return subtitles
    .filter(Boolean)
    .map(str => str.replace(/\s/g, '\u00A0'))
    .join(',\n');
}

function EpisodeCardWithContent({
  isAuth,
  partner,
  className,
  data,
  displayType,
  analyticsLocation,
  page,
  ...props
}) {
  const uniqueId = useSelector(AuthSelectors.getUniqueId);
  const anonymousId = useSelector(AuthSelectors.getAnonymousId);

  const {
    id,
    name,
    season_number,
    episode_number,
    runtime,
    air_date,
    overview,
    user_content_status,
    content,
  } = data;
  const unique_id = uniqueId || anonymousId;
  const externalUrlParams = getExternalUrlParams({ unique_id });
  const isTooltip = displayType === DisplayType.TOOLTIP;
  const hasApps = data.apps?.length > 0;

  function handleExternalLink(e, analyticsData) {
    const property = {
      ...analyticsData,
      location: LOCATIONS_EPISODE_HOVER,
      pageTitle: document.title,
      href: e.target.href || '',
      unique_id,
      episode_id: id,
      episode_name: name,
      content_title: content.content_title,
    };

    externalLink(property);
  }

  function handleWatchNowСlick() {
    const { currentlyWatching } = contentStatuses;

    const needChangeContentStatus = user_content_status !== currentlyWatching;

    if (needChangeContentStatus) {
      props.changeContentStatus(id, currentlyWatching);
    }
  }

  const seasonEpisodeNumber = `S${season_number}E${episode_number}`;
  const yearTitle = getContentYear(air_date);
  const durationTitle = getDuration(runtime);

  const contentSubtitleData = React.useMemo(
    () => [seasonEpisodeNumber, yearTitle, durationTitle],
    [],
  );

  const contentSubtitle = React.useMemo(() => {
    return getStringWithoutSpaces(contentSubtitleData);
  }, [contentSubtitleData]);

  return (
    <EpisodeCardContentStyled
      className={clsx(className, {
        [`episode-content--${displayType}`]: displayType,
        [`episode-content--${page}`]: page,
      })}
    >
      <div className="episode-content__section">
        {isTooltip && (
          <Typography component="h3" className="episode-content__title p3 cut-text">
            {content.content_title} - {name}
          </Typography>
        )}
        <Typography component="h4" className="episode-content__subtitle p5">
          {contentSubtitle}
        </Typography>
        <Divider className="episode-content__divider" />
        <Typography className="episode-content__description p5">
          <Truncate lines={2} ellipsis="..." trimWhitespace>
            {overview}
          </Truncate>
        </Typography>
        {hasApps && (
          <>
            <AppsList
              className="episode-content__apps"
              isAuth={isAuth}
              data={data.apps}
              displayType={displayType}
              externalUrlParams={externalUrlParams}
              handleExternalLink={handleExternalLink}
              handleWatchNowСlick={handleWatchNowСlick}
              analyticsLocation={analyticsLocation}
            />
          </>
        )}
      </div>
    </EpisodeCardContentStyled>
  );
}

EpisodeCardWithContent.defaultProps = {
  className: '',
  isAuth: false,
  displayType: DisplayType.TOOLTIP,
  partner: {},
};

EpisodeCardWithContent.propTypes = {
  data: PropTypes.object.isRequired,
  changeContentStatus: PropTypes.func.isRequired,
  className: PropTypes.string,
  displayType: PropTypes.string,
  page: PropTypes.string,
  analyticsLocation: PropTypes.string,
  isAuth: PropTypes.bool,
  partner: PropTypes.object,
};

export default React.memo(EpisodeCardWithContent);
